import type { ButtonHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import { variantColors, type ButtonVariant } from '../Button/variantColors';
import { BUTTON_SHADOW_PATH, BUTTON_BORDER_PATH, BUTTON_MAIN_PATH } from '../svg-paths';
import { createResponsiveTextStroke } from '@/styles/textStroke';
import { useClickWithSound } from '@/shared/hooks/useClickWithSound';

export interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
}

export function PillButton({
  children,
  className = '',
  variant = 'blue',
  disabled,
  icon,
  onClick,
  ...props
}: PillButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const gradientId = useId();

  const colors = variantColors[variant];
  const activePress = isPressed && !disabled;
  const handleClick = useClickWithSound(onClick, disabled);

  return (
    <button
      className={`relative transition-all duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      style={{ containerType: 'size' }}
      onMouseDown={() => {
        setIsPressed(true);
      }}
      onMouseUp={() => {
        setIsPressed(false);
      }}
      onMouseLeave={() => {
        setIsPressed(false);
      }}
      onTouchStart={() => {
        setIsPressed(true);
      }}
      onTouchEnd={() => {
        setIsPressed(false);
      }}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      <svg
        className="block size-full transition-transform duration-150"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 403 106"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
        aria-hidden="true"
      >
        {/* Shadow/base layer */}
        <path d={BUTTON_SHADOW_PATH} fill={colors.shadow} />
        {/* Black border */}
        <path d={BUTTON_BORDER_PATH} fill="#000" />
        {/* Main gradient button */}
        <path d={BUTTON_MAIN_PATH} fill={`url(#${gradientId})`} />
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="201"
            x2="201"
            y1="89"
            y2="4"
          >
            <stop stopColor={colors.gradient.start} />
            <stop offset="0.49" stopColor={colors.gradient.middle} />
            <stop offset="1" stopColor={colors.gradient.end} />
          </linearGradient>
        </defs>
      </svg>

      <span
        className="absolute inset-0 flex items-center justify-center pointer-events-none transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
      >
        {icon ?? (
          <span
            className="font-mind-boggle text-[#F2F2F2] leading-none"
            style={{
              fontSize: 'clamp(1rem, 20cqw, 2.5rem)',
              fontWeight: 1000,
              ...createResponsiveTextStroke(1, 0.06)
            }}
          >
            {children}
          </span>
        )}
      </span>
    </button>
  );
}

export default PillButton;
